import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { orderChangesApi } from "@/features/orders/api/orderChanges";
import { uploadErrorMessage } from "@/utils/managedFile";
import type {
  WpwOwnerChangeAction, WpwOwnerChangeInput, WpwOwnerChangeRow, WpwPackageBrief, WpwScope,
} from "@/features/orders/types";

// Wybór GK: owner_id → co się z nim dzieje. Pliki trzymamy z nazwą, żeby pokazać je
// na liście (backend dostaje same id). „replace” = zamiana pakietu tego samego właściciela:
// powstaje, gdy właściciel JUŻ w zamówieniu (z nowym pakietem) jest zaznaczony w „Nowe WPW”
// I jego stary WPW w „do usunięcia” — to nie osobny przycisk, tylko para zaznaczeń.
export type WpwPlanDraft = Record<string, { action: WpwOwnerChangeAction; files: { id: string; name: string }[] }>;

const HANDS_FILE: ReadonlySet<WpwOwnerChangeAction> = new Set(["add", "replace"]);

export function wpwPlanToInput(draft: WpwPlanDraft): WpwOwnerChangeInput[] {
  return Object.entries(draft).map(([owner, e]) => ({
    owner,
    action: e.action,
    ...(HANDS_FILE.has(e.action) ? { files: e.files.map((f) => f.id) } : {}),
  }));
}

export function wpwPlanFromRows(rows: WpwOwnerChangeRow[] | undefined): WpwPlanDraft {
  const draft: WpwPlanDraft = {};
  for (const r of rows ?? []) draft[r.owner_id] = { action: r.action, files: r.files };
  return draft;
}

/** Quantity difference of the WPW-count line in the proposed changes (null = the order
 *  has no such line, the list is then free). Mirrors backend wpw_count_delta. */
export function wpwCountDelta(
  scope: WpwScope | undefined,
  changes: { order_line_item_id: string | null; new_quantity?: string }[],
): number | null {
  const line = scope?.count_line_item;
  if (!line) return null;
  const change = changes.find((c) => c.order_line_item_id === line.id);
  const next = change?.new_quantity != null ? parseFloat(change.new_quantity) : NaN;
  return isNaN(next) ? 0 : next - parseFloat(line.quantity);
}

const fmtCount = (n: number) => (Number.isInteger(n) ? String(n) : n.toLocaleString("pl-PL"));

/** Właściciele z zamówienia oferowani w „Nowe WPW” (mają nowy pakiet) — ich „add” to
 *  połowa zamiany, nie nowy WPW. */
const inOrderIdsOf = (scope: WpwScope | undefined) =>
  new Set((scope?.candidates ?? []).filter((c) => c.in_order).map((c) => c.owner_id));

/** Why the plan cannot be sent yet (null = fine). Same NET rule as backend validate_plan:
 *  dodani − usunięci musi odpowiadać zmianie ilości (zamiana pakietu tego samego
 *  właściciela nie liczy się wcale). Inny właściciel w miejsce dotychczasowego = usuń
 *  jeden, dodaj jednego przy ilości bez zmian; „dwa za jeden” = ilość −1, dwa usunięte,
 *  jeden dodany; zwykłe dodanie/usunięcie = stare ±. Dodania przy zwiększeniu zostają
 *  opcjonalne. */
export function wpwPlanIssue(
  scope: WpwScope | undefined,
  delta: number | null,
  draft: WpwPlanDraft,
): string | null {
  if (!scope) return null;
  const inOrder = inOrderIdsOf(scope);
  const entries = Object.entries(draft);
  // właściciel z zamówienia zaznaczony w „Nowe WPW” bez swojego WPW w „do usunięcia”
  const half = entries.filter(([id, e]) => e.action === "add" && inOrder.has(id));
  if (half.length) {
    const names = half.map(([id]) => scope.candidates.find((c) => c.owner_id === id)?.owner_name ?? id);
    return `Już w zamówieniu — żeby zamienić pakiet, zaznacz też WPW do usunięcia tego właściciela: ${names.join(", ")}.`;
  }
  const adds = entries.filter(([id, e]) => e.action === "add" && !inOrder.has(id));
  const removes = entries.filter(([, e]) => e.action === "remove");
  const replaces = entries.filter(([, e]) => e.action === "replace");
  if (delta !== null) {
    const net = adds.length - removes.length;
    const counts = `(dodano ${adds.length}, usunięto ${removes.length})`;
    if (delta === 0 && net !== 0) {
      return `Ilość pozycji WPW się nie zmienia — na każdy nowy WPW musi przypadać jeden usunięty (zamiana pakietu) ${counts}. Żeby dodać WPW, zwiększ ilość; żeby tylko usunąć — zmniejsz.`;
    }
    if (delta > 0 && net < 0) {
      return `Ilość rośnie o ${fmtCount(delta)}, a lista WPW netto maleje ${counts} — każdy usuwany WPW potrzebuje zamiennika wśród nowych.`;
    }
    if (delta > 0 && net > delta) {
      return `Wybierz najwyżej ${fmtCount(delta)} nowych właścicieli ponad usuwanych ${counts}.`;
    }
    if (delta < 0) {
      const needed = Math.min(-delta, scope.handovers.length);
      if (-net !== needed) {
        return `Wskaż ${fmtCount(needed)} WPW do usunięcia z listy${adds.length ? " ponad dodawane" : ""} (wybrano ${removes.length}${adds.length ? `, dodano ${adds.length}` : ""}).`;
      }
    }
  }
  const packages = new Map<string, WpwPackageBrief | null>([
    ...scope.handovers.map((h) => [h.owner_id, h.package] as const),
    ...scope.candidates.map((c) => [c.owner_id, c.package] as const),
  ]);
  const missing = [...adds, ...replaces].filter(([id, e]) => !e.files.length && !packages.get(id)?.usable);
  if (missing.length) return "Każdy nowy lub zamieniany WPW potrzebuje pliku ze zgodą do pozyskania — wgraj plik albo skompiluj pakiet WPW.";
  return null;
}

interface Props {
  scope: WpwScope | undefined;
  loading: boolean;
  delta: number | null;
  value: WpwPlanDraft;
  onChange: (next: WpwPlanDraft) => void;
  disabled?: boolean;
  /** Dlaczego lista jest nieaktywna (np. pozycja liczby WPW nie jest zaznaczona w tabeli). */
  disabledReason?: string;
  /** Właściciele z pakietem z sekwencji masowej WPW — tylko PODŚWIETLENI (na górze listy,
   *  z odznaką). Nikogo nie zaznaczamy i nie ruszamy ilości: to użytkownik decyduje, czy
   *  zamienia pakiet, dodaje nowy, czy zastępuje dwa jednym. */
  highlight?: ReadonlySet<string>;
}

export function WpwListChangeEditor({
  scope, loading, delta, value, onChange, disabled, disabledReason, highlight,
}: Props) {
  const [filter, setFilter] = useState("");
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  const inOrderIds = useMemo(() => inOrderIdsOf(scope), [scope]);
  // Prawdziwe dodania (nowi właściciele); „add” właściciela z zamówienia to połowa zamiany.
  const addCount = Object.entries(value).filter(([id, e]) => e.action === "add" && !inOrderIds.has(id)).length;
  const removeCount = Object.values(value).filter((e) => e.action === "remove").length;
  // Dodać można tyle, o ile rośnie ilość, plus zamienniki za usuwanych. Usuwanie nie ma
  // twardego limitu (to zwykle pierwszy ruch przy zamianie) — pilnuje go komunikat.
  const addLimit = delta === null ? Infinity : removeCount + Math.max(delta, 0);
  const removeNeeded = delta !== null && delta < 0 ? Math.min(-delta, scope?.handovers.length ?? 0) : null;

  // Odznaczenie WPW do usunięcia (albo zmniejszenie ilości) zabiera miejsce już zaznaczonym
  // nowym WPW — nadmiarowe dodania (ostatnio zaznaczone) schodzą same, zamiast wisieć
  // zaznaczone wbrew limitowi. Połówki zamiany (właściciel z zamówienia) zostają.
  useEffect(() => {
    if (addCount <= addLimit) return;
    const next = { ...value };
    let over = addCount - addLimit;
    for (const [id, e] of [...Object.entries(next)].reverse()) {
      if (over <= 0) break;
      if (e.action === "add" && !inOrderIds.has(id)) {
        delete next[id];
        over -= 1;
      }
    }
    onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCount, addLimit]);

  const isHighlighted = (ownerId: string) => !!highlight?.has(ownerId);

  const candidates = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const all = scope?.candidates ?? [];
    const shown = q
      ? all.filter((c) => `${c.owner_name} ${c.parcel_number} ${c.parcel_teryt}`.toLowerCase().includes(q))
      : all;
    if (!highlight?.size) return shown;
    // podświetleni z sekwencji najpierw (stabilnie, reszta w kolejności z serwera)
    return [...shown].sort((a, b) => Number(highlight.has(b.owner_id)) - Number(highlight.has(a.owner_id)));
  }, [scope, filter, highlight]);
  const highlightedCount = useMemo(
    () => new Set(
      [...(scope?.candidates ?? []), ...(scope?.handovers ?? [])]
        .map((c) => c.owner_id)
        .filter((id) => isHighlighted(id)),
    ).size,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope, highlight],
  );

  // „Nowe WPW”: nowy właściciel → add/—. Właściciel z zamówienia (nowy pakiet) jest
  // klikalny DOPIERO, gdy jego stary WPW jest zaznaczony w „do usunięcia” — wtedy para
  // staje się zamianą pakietu (replace); odznaczenie wraca do samego usunięcia.
  const toggleCandidate = (ownerId: string) => {
    const next = { ...value };
    const cur = next[ownerId];
    if (inOrderIds.has(ownerId)) {
      if (cur?.action === "remove") next[ownerId] = { action: "replace", files: [] };
      else if (cur?.action === "replace") next[ownerId] = { action: "remove", files: [] };
      else return; // bez własnego WPW w „do usunięcia” — checkbox jest nieaktywny
    } else if (cur?.action === "add") {
      delete next[ownerId];
    } else {
      next[ownerId] = { action: "add", files: [] };
    }
    onChange(next);
  };
  // „WPW do usunięcia”: remove/—. Odznaczenie WPW zamienianego (replace) zdejmuje całą
  // zamianę — nowy pakiet nie może zostać zaznaczony bez usunięcia starego.
  const toggleHandover = (ownerId: string) => {
    const next = { ...value };
    const cur = next[ownerId];
    if (!cur) next[ownerId] = { action: "remove", files: [] };
    else delete next[ownerId];
    onChange(next);
  };

  const upload = async (ownerId: string, action: WpwOwnerChangeAction, files: FileList | null) => {
    if (!files?.length) return;
    setUploading(ownerId);
    setUploadErr(null);
    try {
      const uploaded = await Promise.all(Array.from(files).map((f) => orderChangesApi.uploadFile(f)));
      const entry = value[ownerId] ?? { action, files: [] };
      onChange({
        ...value,
        [ownerId]: { ...entry, files: [...entry.files, ...uploaded.map((u) => ({ id: u.id, name: u.original_filename }))] },
      });
    } catch (err) {
      setUploadErr(uploadErrorMessage(err, "Nie udało się wgrać pliku — spróbuj ponownie."));
    } finally {
      setUploading(null);
    }
  };

  const dropFile = (ownerId: string, fileId: string) => {
    const entry = value[ownerId];
    if (!entry) return;
    onChange({ ...value, [ownerId]: { ...entry, files: entry.files.filter((f) => f.id !== fileId) } });
  };

  // Pliki ze zgodą przy dodawanym / zamienianym WPW: lista wgranych + wgranie kolejnego.
  const renderFiles = (ownerId: string, entry: WpwPlanDraft[string], pkg: WpwPackageBrief | null) => (
    <div className="pl-6 mt-1 flex flex-col gap-1">
      {entry.files.map((f) => (
        <span key={f.id} className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
          📎 {f.name}
          <button
            type="button"
            onClick={() => dropFile(ownerId, f.id)}
            className="text-red-400 hover:text-red-600"
            title="Usuń plik"
          >
            ×
          </button>
        </span>
      ))}
      <label className="text-[var(--color-primary)] hover:underline cursor-pointer w-fit">
        {uploading === ownerId
          ? "Wgrywanie…"
          : entry.files.length
            ? "+ Dodaj plik"
            : pkg?.usable
              ? "Zamiast pakietu: wgraj własny plik ze zgodą"
              : "+ Wgraj plik ze zgodą"}
        <input
          type="file"
          multiple
          className="hidden"
          disabled={disabled || uploading !== null}
          onChange={(e) => { void upload(ownerId, entry.action, e.target.files); e.target.value = ""; }}
        />
      </label>
    </div>
  );

  if (loading) {
    return <div className="flex justify-center py-3"><Spinner /></div>;
  }
  if (!scope) return null;

  const lineName = scope.count_line_item?.name;

  // Podpis sekcji „Nowe WPW” — ile można jeszcze wskazać i z jakiego powodu.
  const addHint = (() => {
    if (delta === null) return `— wybrano ${addCount}`;
    if (delta === 0 && removeCount === 0) return "— zwiększ ilość albo wskaż WPW do usunięcia (zamiana)";
    return `— wybrano ${addCount} z ${fmtCount(addLimit)}${delta > 0 && removeCount === 0 ? " (opcjonalnie)" : ""}`;
  })();
  const removeMismatch = removeNeeded !== null && removeCount - addCount !== removeNeeded;
  const removeHint = (() => {
    if (removeNeeded !== null) {
      return `— wskaż ${fmtCount(removeNeeded)}${addCount ? ` ponad ${addCount} dodawanych` : ""} (wybrano ${removeCount})`;
    }
    if (removeCount) return `— wybrano ${removeCount}`;
    return "";
  })();

  return (
    <div
      className={`rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-900/10 p-3 flex flex-col gap-2 ${disabled ? "opacity-70" : ""}`}
      data-testid="wpw-list-editor"
      aria-disabled={disabled || undefined}
    >
      <div>
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Lista WPW do pozyskania</p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          Nie jest częścią wniosku — podwykonawca zobaczy zmianę na liście WPW dopiero po akceptacji wniosku.
        </p>
      </div>

      {disabled && disabledReason && (
        <p className="text-[11px] text-amber-700 dark:text-amber-400" data-testid="wpw-disabled-hint">
          {disabledReason}
        </p>
      )}

      {highlightedCount > 0 && (
        <p className="text-[11px] text-emerald-800 dark:text-emerald-300" data-testid="wpw-highlight-hint">
          Podświetleni właściciele ({highlightedCount}) mają pakiet z sekwencji masowej WPW. Zamiana pakietu: zaznacz
          właściciela w „Nowe WPW” i jego dotychczasowy WPW w „do usunięcia” (ilość bez zmian). Nowy WPW: zwiększ
          ilość. Dwa pakiety za jeden: zmniejsz ilość, usuń dwa WPW, dodaj jednego.
        </p>
      )}

      {!disabled && delta === 0 && lineName && highlightedCount === 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Ilość pozycji „{lineName}” bez zmian: zamiana pakietu = właściciel w „Nowe WPW” + WPW w „do usunięcia”.
          Żeby dodać nowe WPW, zwiększ ilość; żeby tylko usunąć — zmniejsz.
        </p>
      )}

      <div>
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-xs text-gray-700 dark:text-gray-300">
            Nowe WPW <span className="text-gray-400">{addHint}</span>
          </span>
          {(scope.candidates.length > 8) && (
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Szukaj właściciela / działki"
              className="w-48 border border-gray-300 dark:border-gray-600 rounded px-2 py-0.5 text-xs bg-white dark:bg-gray-700"
            />
          )}
        </div>
        {scope.candidates.length === 0 ? (
          <p className="text-xs text-gray-400">Brak właścicieli, których można dodać (wszyscy są już w zamówieniu albo mają zgodę).</p>
        ) : (
          <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            {candidates.map((c) => {
              const entry = value[c.owner_id];
              const checked = entry?.action === "add" || entry?.action === "replace";
              // właściciel z zamówienia: klikalny tylko z własnym WPW zaznaczonym w „do usunięcia”
              // (zamiana nie zajmuje miejsca nowym WPW); nowy właściciel — do limitu dodań
              const needsOwnRemoval = c.in_order && !checked && entry?.action !== "remove";
              const full = !c.in_order && !checked && addCount >= addLimit;
              const lit = isHighlighted(c.owner_id);
              return (
                <li
                  key={c.owner_id}
                  className={`px-2 py-1.5 text-xs ${lit ? "bg-emerald-50 dark:bg-emerald-900/25" : ""}`}
                  data-testid={`wpw-candidate-${c.owner_id}`}
                  data-highlighted={lit ? "true" : undefined}
                >
                  <label
                    className={`flex items-start gap-2 ${full || needsOwnRemoval || disabled ? "opacity-50" : "cursor-pointer"}`}
                    title={needsOwnRemoval ? "Najpierw zaznacz WPW tego właściciela w „do usunięcia” — razem to zamiana pakietu." : undefined}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      disabled={disabled || full || needsOwnRemoval}
                      onChange={() => toggleCandidate(c.owner_id)}
                    />
                    <span className="flex-1">
                      <span className="text-gray-800 dark:text-gray-200">{c.owner_name}</span>
                      <span className="text-gray-400"> · dz. {c.parcel_number || c.parcel_teryt}</span>
                      {c.ownership_share && <span className="text-gray-400"> · udział {c.ownership_share}</span>}
                      {lit && <Badge color="green" className="ml-1">pakiet z sekwencji masowej</Badge>}
                      {c.in_order && <Badge color="blue" className="ml-1">w zamówieniu — nowy pakiet</Badge>}
                      {entry?.action === "replace" && <Badge color="green" className="ml-1">zamiana pakietu</Badge>}
                      {c.in_field && <Badge color="yellow" className="ml-1">w terenie (inne zlecenie)</Badge>}
                      {c.in_pending_request && <Badge color="gray" className="ml-1">w innym wniosku</Badge>}
                      <span className="block text-[11px]">
                        {c.package?.usable ? (
                          <span className="text-emerald-700 dark:text-emerald-400">
                            Pakiet {c.package.number} — trafi do podwykonawcy jako plik ze zgodą
                          </span>
                        ) : (
                          <span className="text-amber-700 dark:text-amber-400">
                            {c.package ? `Pakiet ${c.package.number}: ${c.package.reason}` : "Brak pakietu WPW"} — wgraj plik ze zgodą
                          </span>
                        )}
                      </span>
                    </span>
                  </label>
                  {checked && renderFiles(c.owner_id, entry, c.package)}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div>
        <p className="text-xs text-gray-700 dark:text-gray-300 mb-1">
          WPW do usunięcia
          {removeHint && (
            <span className={removeMismatch ? "text-red-600" : "text-gray-400"}> {removeHint}</span>
          )}
        </p>
        {scope.handovers.length === 0 ? (
          <p className="text-xs text-gray-400">Zamówienie nie ma jeszcze żadnego WPW.</p>
        ) : (
          <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            {scope.handovers.map((h) => {
              const entry = value[h.owner_id];
              const checked = entry?.action === "remove" || entry?.action === "replace";
              const blocked = !!h.block_reason;
              const lit = isHighlighted(h.owner_id);
              const swappable = inOrderIds.has(h.owner_id);
              return (
                <li
                  key={h.handover_id}
                  className={`px-2 py-1.5 text-xs ${lit ? "bg-emerald-50 dark:bg-emerald-900/25" : ""}`}
                  data-testid={`wpw-handover-${h.owner_id}`}
                  data-highlighted={lit ? "true" : undefined}
                >
                  <label
                    className={`flex items-start gap-2 ${blocked || disabled ? "opacity-50" : "cursor-pointer"}`}
                    title={h.block_reason || undefined}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      disabled={disabled || blocked}
                      onChange={() => toggleHandover(h.owner_id)}
                    />
                    <span className="flex-1">
                      <span className="text-gray-800 dark:text-gray-200">{h.owner_name}</span>
                      <span className="text-gray-400"> · dz. {h.parcel_number || h.parcel_teryt}</span>
                      <span className="text-gray-400"> · {h.declared_status_display}</span>
                      {h.active_files > 0 && <span className="text-gray-400"> · plików od podwykonawcy: {h.active_files}</span>}
                      {lit && <Badge color="green" className="ml-1">nowy pakiet w sekwencji masowej</Badge>}
                      {entry?.action === "replace" && <Badge color="green" className="ml-1">zamiana pakietu</Badge>}
                      {h.in_pending_request && <Badge color="gray" className="ml-1">w innym wniosku</Badge>}
                      {blocked && <span className="block text-[11px] text-amber-700 dark:text-amber-400">{h.block_reason}</span>}
                      {swappable && h.package && entry?.action !== "replace" && (
                        <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                          Nowy pakiet {h.package.number} czeka w „Nowe WPW” — zaznacz oba, żeby zamienić pakiet.
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {removeCount > 0 && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            Po akceptacji wskazane WPW wypadną z zamówienia. Właściciel bez innego zamówienia zostaje w zakładce WPW projektu z uwagą „nie ma w żadnym zamówieniu” — z historią i plikami; koordynator może tam sam wgrać zgodę lub odmowę.
          </p>
        )}
      </div>

      {uploadErr && <p className="text-xs text-red-600">{uploadErr}</p>}
    </div>
  );
}

/** Read-only summary of a remembered list on a request card (GK only). */
export function WpwListChangeSummary({ rows }: { rows: WpwOwnerChangeRow[] }) {
  if (!rows.length) return null;
  const adds = rows.filter((r) => r.action === "add");
  const removes = rows.filter((r) => r.action === "remove");
  const replaces = rows.filter((r) => r.action === "replace");
  const label = (r: WpwOwnerChangeRow) => `${r.owner_name} (dz. ${r.parcel_number || r.parcel_teryt})`;
  const withFile = (r: WpwOwnerChangeRow) =>
    label(r) + (r.files.length ? ` — ${r.files.map((f) => f.name).join(", ")}` : " — pakiet WPW");
  return (
    <div className="mt-2 rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-900/10 px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300">
      <span className="font-medium">Lista WPW</span>
      <span className="text-gray-400"> (wewnętrzne — stosowane przy akceptacji)</span>
      {adds.length > 0 && <div>Dodanie: {adds.map(withFile).join("; ")}</div>}
      {replaces.length > 0 && <div>Zamiana pakietu: {replaces.map(withFile).join("; ")}</div>}
      {removes.length > 0 && <div>Usunięcie: {removes.map(label).join("; ")}</div>}
    </div>
  );
}
